import { useEffect, useRef, useState } from 'react'

export interface RowMenuItem {
  label: string
  onSelect: () => void
  /** Styled as a destructive action (`.rowmenu button.danger`). */
  danger?: boolean
}

/**
 * A "⋯" button that opens a small action menu — the LeftNav row-menu pattern (`.row-kebab` +
 * `.rowmenu`, closed by a click anywhere outside or by Escape), packaged on its own so a card can
 * carry one without owning the open/close state. LeftNav keeps its inline copy for now: its three
 * menus share one `menuFor` state with a right-click entry point, which is a larger refactor.
 *
 * Keyboard: Enter/Space on the button opens the menu and focuses the first item; Up/Down/Home/End
 * move between items; Escape closes it and puts focus back on the button, as does choosing an item
 * (so a dialog the item opens restores focus there when it closes). Tab moves on and closes it.
 *
 * Clicks never bubble out of it: it sits on cards that open something when clicked.
 */
export function RowMenu({
  label,
  items,
  className,
}: {
  /** The button's accessible name, e.g. "Bid actions". */
  label: string
  items: RowMenuItem[]
  className?: string
}) {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLSpanElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)

  const itemButtons = (): HTMLButtonElement[] =>
    [...(wrapRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]') ?? [])]

  const close = (refocus: boolean): void => {
    setOpen(false)
    if (refocus) buttonRef.current?.focus()
  }

  // Focus the first item as the menu opens, so the keyboard lands inside it.
  useEffect(() => {
    if (open) itemButtons()[0]?.focus()
  }, [open])

  // A click anywhere outside closes it, without swallowing that click (see LeftNav.tsx).
  useEffect(() => {
    if (!open) return
    const closeIfOutside = (e: MouseEvent): void => {
      if (wrapRef.current?.contains(e.target as Node)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', closeIfOutside)
    return () => document.removeEventListener('mousedown', closeIfOutside)
  }, [open])

  function onMenuKeyDown(e: React.KeyboardEvent): void {
    const list = itemButtons()
    const at = list.indexOf(document.activeElement as HTMLButtonElement)
    let next: number | null = null
    if (e.key === 'ArrowDown') next = (at + 1) % list.length
    else if (e.key === 'ArrowUp') next = (at - 1 + list.length) % list.length
    else if (e.key === 'Home') next = 0
    else if (e.key === 'End') next = list.length - 1
    else if (e.key === 'Escape') {
      e.preventDefault()
      close(true)
      return
    } else if (e.key === 'Tab') {
      // After the browser has moved focus on from the item: unmounting the menu first would drop
      // focus to <body> and restart the Tab order from the top of the page.
      setTimeout(() => setOpen(false), 0)
      return
    }
    if (next !== null) {
      e.preventDefault()
      list[next]?.focus()
    }
  }

  return (
    <span
      ref={wrapRef}
      className={'rowmenu-wrap' + (className ? ` ${className}` : '')}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <button
        ref={buttonRef}
        type="button"
        className="row-kebab"
        aria-label={label}
        title={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === 'Escape' && open) close(true)
        }}
      >
        ⋯
      </button>
      {open && (
        <div className="rowmenu" role="menu" aria-label={label} onKeyDown={onMenuKeyDown}>
          {items.map((item) => (
            <button
              key={item.label}
              type="button"
              role="menuitem"
              tabIndex={-1}
              className={item.danger ? 'danger' : undefined}
              onClick={() => {
                close(true)
                item.onSelect()
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </span>
  )
}
